import React, { useState, useEffect } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import { MenuItem, FormControl, InputLabel, Select } from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { InputAdornment, IconButton } from "@material-ui/core";
import QueueSelectSingle from "../../components/QueueSelectSingle";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const OPENAI_VOICE_OPTIONS = [
    { value: "alloy", label: "Alloy - M" },
    { value: "ash", label: "Ash - M" },
    { value: "ballad", label: "Ballad - M" },
    { value: "coral", label: "Coral - F" },
    { value: "echo", label: "Echo - M" },
    { value: "fable", label: "Fable - M" },
    { value: "nova", label: "Nova - M" },
    { value: "onyx", label: "Onyx - F" },
    { value: "sage", label: "Sage - F" },
    { value: "shimmer", label: "Shimmer - F" }
];

const AZURE_VOICE_OPTIONS = [
    { value: "pt-BR-FranciscaNeural", label: "Francisca" },
    { value: "pt-BR-AntonioNeural", label: "Antônio" },
    { value: "pt-BR-BrendaNeural", label: "Brenda" },
    { value: "pt-BR-DonatoNeural", label: "Donato" },
    { value: "pt-BR-ElzaNeural", label: "Elza" },
    { value: "pt-BR-FabioNeural", label: "Fábio" },
    { value: "pt-BR-GiovannaNeural", label: "Giovanna" },
    { value: "pt-BR-HumbertoNeural", label: "Humberto" },
    { value: "pt-BR-JulioNeural", label: "Julio" },
    { value: "pt-BR-LeilaNeural", label: "Leila" },
    { value: "pt-BR-LeticiaNeural", label: "Letícia" },
    { value: "pt-BR-ManuelaNeural", label: "Manuela" },
    { value: "pt-BR-NicolauNeural", label: "Nicolau" },
    { value: "pt-BR-ValerioNeural", label: "Valério" },
    { value: "pt-BR-YaraNeural", label: "Yara" }
];

const DEFAULT_AZURE_VOICE = "pt-BR-FabioNeural";
const DEFAULT_OPENAI_VOICE = "onyx";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    multFieldLine: {
        display: "flex",
        "& > *:not(:last-child)": {
            marginRight: theme.spacing(1),
        },
    },

    btnWrapper: {
        position: "relative",
    },

    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
    },
    colorAdorment: {
        width: 20,
        height: 20,
    },
}));

const PromptSchema = Yup.object().shape({
    name: Yup.string().min(5, "Muito curto!").max(100, "Muito longo!").required("Obrigatório"),
    prompt: Yup.string().min(50, "Muito curto!").required("Descreva o treinamento para Inteligência Artificial"),
    voice: Yup.string().required("Informe o modo para Voz"),
    max_tokens: Yup.number().required("Informe o número máximo de tokens"),
    temperature: Yup.number().required("Informe a temperatura"),
    apikey: Yup.string().required("Informe a API Key"),
    queueId: Yup.number().required("Informe a fila"),
    max_messages: Yup.number().required("Informe o número máximo de mensagens")
});

const PromptModal = ({ open, onClose, promptId }) => {
    const classes = useStyles();
    const [selectedVoice, setSelectedVoice] = useState(DEFAULT_AZURE_VOICE);
    const [voiceMode, setVoiceMode] = useState("ambos");
    const [voicePriority, setVoicePriority] = useState("texto");
    const [showApiKey, setShowApiKey] = useState(false);
    const [voiceKey, setVoiceKey] = useState("");
    const isOpenAiKey = voiceKey && String(voiceKey).startsWith("sk-");
    const availableVoiceOptions = isOpenAiKey ? OPENAI_VOICE_OPTIONS : AZURE_VOICE_OPTIONS;

    const handleToggleApiKey = () => {
        setShowApiKey(!showApiKey);
    };

    const initialState = {
        name: "",
        prompt: "",
        voice: DEFAULT_AZURE_VOICE,
        voiceKey: "",
        voiceRegion: "",
        voiceMode: "ambos",
        voicePriority: "texto",
        maxTokens: 100,
        temperature: 1,
        apiKey: "",
        queueId: null,
        maxMessages: 10
    };

    const [prompt, setPrompt] = useState(initialState);

    useEffect(() => {
        const fetchPrompt = async () => {
            if (!promptId) {
                setPrompt(initialState);
                setSelectedVoice(DEFAULT_AZURE_VOICE);
                setVoiceMode("ambos");
                setVoicePriority("texto");
                setVoiceKey("");
                return;
            }
            try {
                const { data } = await api.get(`/prompt/${promptId}`);
                const fetchedIsOpenAiKey = Boolean(data.voiceKey && String(data.voiceKey).startsWith("sk-"));
                const fallbackVoice = fetchedIsOpenAiKey ? DEFAULT_OPENAI_VOICE : DEFAULT_AZURE_VOICE;
                setPrompt(prevState => {
                    return { ...prevState, ...data };
                });
                setSelectedVoice(data.voice || fallbackVoice);
                setVoiceMode(data.voiceMode || "ambos");
                setVoicePriority(data.voicePriority || "texto");
                setVoiceKey(data.voiceKey || "");
            } catch (err) {
                toastError(err);
            }
        };

        fetchPrompt();
    }, [promptId, open]);

    const handleClose = () => {
        setPrompt(initialState);
        setSelectedVoice(DEFAULT_AZURE_VOICE);
        setVoiceMode("ambos");
        setVoicePriority("texto");
        setVoiceKey("");
        onClose();
    };

    const handleChangeVoice = (e) => {
        setSelectedVoice(e.target.value);
    };

    const handleChangeVoiceMode = (e) => {
        setVoiceMode(e.target.value);
    };

    const handleChangeVoicePriority = (e) => {
        setVoicePriority(e.target.value);
    };

    const handleChangeVoiceKey = (e) => {
        const nextVoiceKey = e.target.value;
        const nextIsOpenAiKey = Boolean(nextVoiceKey && String(nextVoiceKey).startsWith("sk-"));
        const nextVoiceOptions = nextIsOpenAiKey ? OPENAI_VOICE_OPTIONS : AZURE_VOICE_OPTIONS;
        const hasCompatibleSelectedVoice = nextVoiceOptions.some(option => option.value === selectedVoice);

        setVoiceKey(nextVoiceKey);

        if (!hasCompatibleSelectedVoice) {
            setSelectedVoice(nextIsOpenAiKey ? DEFAULT_OPENAI_VOICE : DEFAULT_AZURE_VOICE);
        }
    };

    useEffect(() => {
        if (voiceMode === "so-texto") {
            return;
        }

        const hasCompatibleSelectedVoice = availableVoiceOptions.some(option => option.value === selectedVoice);

        if (!hasCompatibleSelectedVoice) {
            setSelectedVoice(isOpenAiKey ? DEFAULT_OPENAI_VOICE : DEFAULT_AZURE_VOICE);
        }
    }, [availableVoiceOptions, isOpenAiKey, selectedVoice, voiceMode]);

    const handleSavePrompt = async values => {
        const promptData = {
            ...values,
            voice: selectedVoice,
            voiceMode: voiceMode,
            voicePriority: voicePriority,
            voiceKey: voiceKey
        };
        if (!values.queueId) {
            toastError("Informe o setor");
            return;
        }
        try {
            if (promptId) {
                await api.put(`/prompt/${promptId}`, promptData);
            } else {
                await api.post("/prompt", promptData);
            }
            toast.success(i18n.t("promptModal.success"));
        } catch (err) {
            toastError(err);
        }
        handleClose();
    };

    return (
        <div className={classes.root}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                scroll="paper"
                fullWidth
            >
                <DialogTitle id="form-dialog-title">
                    {promptId
                        ? `${i18n.t("promptModal.title.edit")}`
                        : `${i18n.t("promptModal.title.add")}`}
                </DialogTitle>
                <Formik
                    initialValues={prompt}
                    enableReinitialize={true}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSavePrompt(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting, values }) => (
                        <Form style={{ width: "100%" }}>
                            <DialogContent dividers>
                                <Field
                                    as={TextField}
                                    label={i18n.t("promptModal.form.name")}
                                    name="name"
                                    error={touched.name && Boolean(errors.name)}
                                    helperText={touched.name && errors.name}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                    required
                                />
                                <FormControl fullWidth margin="dense" variant="outlined">
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.apikey")}
                                        name="apiKey"
                                        type={showApiKey ? 'text' : 'password'}
                                        error={touched.apiKey && Boolean(errors.apiKey)}
                                        helperText={touched.apiKey && errors.apiKey}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        required
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={handleToggleApiKey}>
                                                        {showApiKey ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </FormControl>
                                <Field
                                    as={TextField}
                                    label={i18n.t("promptModal.form.prompt")}
                                    name="prompt"
                                    error={touched.prompt && Boolean(errors.prompt)}
                                    helperText={touched.prompt && errors.prompt}
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                    required
                                    rows={10}
                                    multiline={true}
                                />
                                <QueueSelectSingle />

                                <div style={{ marginBottom: "16px" }}>
                                    <h4 style={{ marginBottom: "12px" }}>Configuração de Voz</h4>
                                </div>

                                <div className={classes.multFieldLine}>
                                    <FormControl fullWidth margin="dense" variant="outlined">
                                        <InputLabel>Estratégia de Voz</InputLabel>
                                        <Select
                                            id="voice-mode-select"
                                            labelWidth={130}
                                            value={voiceMode}
                                            onChange={handleChangeVoiceMode}
                                            multiple={false}
                                        >
                                            <MenuItem value="so-voz">Só Voz</MenuItem>
                                            <MenuItem value="so-texto">Só Texto</MenuItem>
                                            <MenuItem value="ambos">Texto e Voz</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <FormControl fullWidth margin="dense" variant="outlined">
                                        <InputLabel>Prioridade</InputLabel>
                                        <Select
                                            id="voice-priority-select"
                                            labelWidth={80}
                                            value={voicePriority}
                                            onChange={handleChangeVoicePriority}
                                            multiple={false}
                                        >
                                            <MenuItem value="texto">Priorizar Texto</MenuItem>
                                            <MenuItem value="voz">Priorizar Voz</MenuItem>
                                        </Select>
                                    </FormControl>
                                </div>

                                <div className={classes.multFieldLine}>
                                    <FormControl fullWidth margin="dense" variant="outlined">
                                        <InputLabel>{isOpenAiKey ? "Voz da Resposta (OpenAI)" : "Voz da Resposta (Azure)"}</InputLabel>
                                        <Select
                                            id="voice-select"
                                            labelWidth={200}
                                            value={selectedVoice}
                                            onChange={handleChangeVoice}
                                            multiple={false}
                                            disabled={voiceMode === "so-texto"}
                                        >
                                            {availableVoiceOptions.map(option => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>

                                <div className={classes.multFieldLine}>
                                    <Field
                                        as={TextField}
                                        label="Chave API de Voz"
                                        name="voiceKey"
                                        type="password"
                                        value={voiceKey}
                                        onChange={handleChangeVoiceKey}
                                        error={touched.voiceKey && Boolean(errors.voiceKey)}
                                        helperText={touched.voiceKey && errors.voiceKey || "OpenAI: sk-... | Azure: subscription key"}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        disabled={voiceMode === "so-texto"}
                                    />

                                    {!isOpenAiKey && (
                                        <Field
                                            as={TextField}
                                            label="Região de Voz (Azure)"
                                            name="voiceRegion"
                                            error={touched.voiceRegion && Boolean(errors.voiceRegion)}
                                            helperText={touched.voiceRegion && errors.voiceRegion || "Ex: brazilsouth, eastus"}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            disabled={voiceMode === "so-texto"}
                                        />
                                    )}
                                </div>

                                <div style={{ marginBottom: "16px", marginTop: "16px" }}>
                                    <h4 style={{ marginBottom: "12px" }}>Configuração de Chat</h4>
                                </div>

                                <div className={classes.multFieldLine}>
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.temperature")}
                                        name="temperature"
                                        error={touched.temperature && Boolean(errors.temperature)}
                                        helperText={touched.temperature && errors.temperature}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                    />
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.max_tokens")}
                                        name="maxTokens"
                                        error={touched.maxTokens && Boolean(errors.maxTokens)}
                                        helperText={touched.maxTokens && errors.maxTokens}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                    />
                                    <Field
                                        as={TextField}
                                        label={i18n.t("promptModal.form.max_messages")}
                                        name="maxMessages"
                                        error={touched.maxMessages && Boolean(errors.maxMessages)}
                                        helperText={touched.maxMessages && errors.maxMessages}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                    />
                                </div>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={handleClose}
                                    color="secondary"
                                    disabled={isSubmitting}
                                    variant="outlined"
                                >
                                    {i18n.t("promptModal.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className={classes.btnWrapper}
                                >
                                    {promptId
                                        ? `${i18n.t("promptModal.buttons.okEdit")}`
                                        : `${i18n.t("promptModal.buttons.okAdd")}`}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default PromptModal;
